import { useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { DisplayFormattedDate } from 'components/common';

import type { GroupCourse } from 'shared-types';

interface Props {
  targetSchedule: Pick<
    GroupCourse,
    | 'deadlineDate'
    | 'deadlineDurationUnit'
    | 'deadlineDurationValue'
    | 'deadlineType'
  >;
}

/**
 * The target schedule a group has for a course, as table text: a date,
 * "7 days after being added", "No target schedule", or "Default value" when
 * the group uses the course's schedule.
 */
const GroupCourseTargetSchedule = ({ targetSchedule }: Props) => {
  const { messages } = useReduxState();

  const {
    deadlineDate,
    deadlineDurationUnit,
    deadlineDurationValue,
    deadlineType
  } = targetSchedule;

  if (deadlineType === 'date' && deadlineDate) {
    return <DisplayFormattedDate date={deadlineDate} />;
  }

  if (
    deadlineType === 'duration' &&
    deadlineDurationUnit &&
    deadlineDurationValue
  ) {
    const durationMessage = {
      days: messages.courseTargetScheduleDaysAfterAdded,
      weeks: messages.courseTargetScheduleWeeksAfterAdded,
      months: messages.courseTargetScheduleMonthsAfterAdded
    }[deadlineDurationUnit];

    return (
      <>
        {getMessageWithPlaceholders({
          message: durationMessage,
          placeholders: { number: `${deadlineDurationValue}` }
        })}
      </>
    );
  }

  if (deadlineType === 'none') {
    return <>{messages.courseTargetScheduleNone}</>;
  }

  return <>{messages.commonDefaultValue}</>;
};

export default GroupCourseTargetSchedule;
